import apiService from '../apiService';

/**
 * 獲取用戶列表
 * @param {Object} params - 查詢參數
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @param {string} params.search - 搜尋關鍵字
 * @param {string} params.status - 用戶狀態
 * @returns {Promise} API 響應
 */
export const getUsers = async (params = {}) => {
    const url = '/api/admin/users';
    return apiService.GET(url, params);
};

/**
 * 獲取單一用戶詳情
 * @param {number} userId - 用戶 ID
 * @returns {Promise} API 響應
 */
export const getUserDetail = async (userId) => {
    const url = `/api/admin/users/${userId}`;
    return apiService.GET(url);
};

/**
 * 創建新用戶
 * @param {Object} userData - 用戶資料
 * @param {string} userData.name - 用戶名稱
 * @param {string} userData.email - Email
 * @param {string} userData.password - 密碼
 * @param {string} userData.phone - 手機號碼
 * @param {number} userData.level_id - 等級 ID
 * @param {number} userData.department_id - 部門 ID
 * @param {number} userData.rank_id - 職級 ID
 * @returns {Promise} API 響應
 */
export const createUser = async (userData) => {
    const url = '/api/admin/users';
    return apiService.POST(url, userData);
};

/**
 * 更新用戶經驗值
 * @param {number} userId - 用戶 ID
 * @param {Object} data - 經驗值資料
 * @param {number} data.exp - 經驗值
 * @returns {Promise} API 響應
 */
export const updateUserExp = async (userId, data) => {
    const url = `/api/admin/user/${userId}/exp`;
    return apiService.PUT(url, data);
};

/**
 * 導出用戶資料
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應 (Blob)
 */
export const exportUsers = async (params = {}) => {
    try {
        // 取得 localStorage 中的 accessToken
        const accessToken = localStorage.getItem('accessToken');

        // 設定 headers，包含 token
        const headers = {
            Accept: 'text/csv, application/octet-stream, */*'
        };

        // 如果有 accessToken，則加入 Authorization header
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }

        // 直接使用 apiService，配置 responseType 為 blob
        const response = await apiService({
            method: 'get',
            url: '/api/admin/users/export',
            params,
            responseType: 'blob',
            headers
        });

        return response.data;
    } catch (error) {
        // 如果是錯誤回應，嘗試解析錯誤訊息
        if (error.response && error.response.data instanceof Blob) {
            let errorText = '';
            try {
                errorText = await error.response.data.text();
                const errorData = JSON.parse(errorText);
                throw new Error(
                    errorData.message || errorData.error || '匯出失敗'
                );
            } catch (e) {
                // 如果 e 是我們剛剛 throw 的 Error，直接再 throw 出去
                if (
                    e.message &&
                    e.message !== '匯出失敗' &&
                    !e.message.includes('JSON')
                ) {
                    throw e;
                }
                throw new Error(
                    `匯出失敗: ${error.response.status} ${error.response.statusText || ''}`
                );
            }
        }
        throw error;
    }
};

/**
 * 導入用戶資料
 * @param {FormData} formData - 包含檔案的表單資料
 * @param {{ useAsync?: boolean }} [options] - useAsync 為 true 時加 ?async=true（大檔用非同步）；未傳或 false 用同步
 * @returns {Promise} API 響應
 */
export const importUsers = async (formData, options = {}) => {
    const base = '/api/admin/users/import';
    const url = options.useAsync ? `${base}?async=true` : base;
    return apiService.POST(url, formData, {
        ContentType: 'multipart/form-data'
    });
};

/**
 * 查詢匯入任務狀態（輪詢用）
 * @param {number} importId - 匯入任務 ID
 * @returns {Promise} API 響應，data 含 status、success_count、failure_count、error_message 等
 */
export const getImportStatus = async (importId) => {
    return apiService.GET(`/api/admin/users/imports/${importId}`);
};

/**
 * 下載匯入範本
 * @returns {Promise} API 響應 (Blob)
 */
export const downloadImportTemplate = async () => {
    try {
        // 取得 localStorage 中的 accessToken
        const accessToken = localStorage.getItem('accessToken');

        // 設定 headers，包含 token
        const headers = {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*'
        };

        // 如果有 accessToken，則加入 Authorization header
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }

        // 直接使用 apiService，配置 responseType 為 blob
        const response = await apiService({
            method: 'get',
            url: '/api/admin/users/import-template',
            responseType: 'blob',
            headers
        });

        return response.data;
    } catch (error) {
        // 如果是錯誤回應，嘗試解析錯誤訊息
        if (error.response && error.response.data instanceof Blob) {
            let errorText = '';
            try {
                errorText = await error.response.data.text();
                const errorData = JSON.parse(errorText);
                throw new Error(
                    errorData.message || errorData.error || '下載範本失敗'
                );
            } catch (e) {
                if (
                    e.message &&
                    e.message !== '下載範本失敗' &&
                    !e.message.includes('JSON')
                ) {
                    throw e;
                }
                throw new Error(
                    `下載範本失敗: ${error.response.status} ${error.response.statusText || ''}`
                );
            }
        }
        throw error;
    }
};

/**
 * 搜尋會員（用於下拉選單）
 * @param {Object} params - 查詢參數
 * @param {string} params.search - 搜尋關鍵字
 * @param {number} params.limit - 限制筆數
 * @returns {Promise} API 響應
 */
export const searchUsers = async (params = {}) => {
    const url = '/api/admin/users/search';
    return apiService.GET(url, params);
};

/**
 * 更新用戶資料（支援夥伴屬性調整）
 * @param {number} userId - 用戶 ID
 * @param {Object} userData - 要更新的用戶資料
 * @returns {Promise} API 響應
 */
export const updateUser = async (userId, userData) => {
    const url = `/api/admin/users/${userId}`;
    return apiService.PUT(url, userData);
};

/**
 * 刪除用戶
 * @param {number} userId - 用戶 ID
 * @returns {Promise} API 響應
 */
export const deleteUser = async (userId) => {
    const url = `/api/admin/users/${userId}`;
    return apiService.DELETE(url);
};

/**
 * 重置用戶密碼
 * @param {number} userId - 用戶 ID
 * @param {Object} data - 密碼資料
 * @param {string} data.password - 新密碼（最少 6 字元）
 * @returns {Promise} API 響應
 */
export const resetUserPassword = async (userId, data) => {
    const url = `/api/admin/users/${userId}`;
    return apiService.PUT(url, data);
};

/**
 * 推薦/輔導異動紀錄查詢
 * @param {Object} params
 * @param {number} params.user_id - 會員ID
 * @param {string} [params.type] - 類型: referral|mentor|all
 * @param {string} [params.changed_from] - 變動起始日期 (YYYY-MM-DD)
 * @param {string} [params.changed_to] - 變動結束日期 (YYYY-MM-DD)
 * @param {number} [params.per_page] - 每頁筆數
 */
export const getReferralLogs = async (params = {}) => {
    const url = '/api/admin/logs/referral';
    return apiService.GET(url, params);
};

/**
 * 事業部異動紀錄查詢
 * @param {Object} params
 * @param {number} params.user_id - 會員ID
 * @param {string} [params.changed_from] - 變動起始日期 (YYYY-MM-DD)
 * @param {string} [params.changed_to] - 變動結束日期 (YYYY-MM-DD)
 * @param {number} [params.per_page] - 每頁筆數
 */
export const getDepartmentLogs = async (params = {}) => {
    const url = '/api/admin/logs/department';
    return apiService.GET(url, params);
};

/**
 * 聘階異動紀錄查詢
 * @param {Object} params
 * @param {number} params.user_id - 會員ID
 * @param {string} [params.assigned_from] - 分配起始日期 (YYYY-MM-DD)
 * @param {string} [params.assigned_to] - 分配結束日期 (YYYY-MM-DD)
 * @param {number} [params.per_page] - 每頁筆數
 */
export const getRankLogs = async (params = {}) => {
    const url = '/api/admin/logs/rank';
    return apiService.GET(url, params);
};

/**
 * 取得會員的組織圖資料（以會員為根節點）
 * @param {number} userId - 會員 ID
 * @param {Object} params
 * @param {string} [params.type] - 線路類型 referral|mentor，預設 referral
 */
export const getUserOrganizationChart = async (userId, params = {}) => {
    const url = `/api/admin/users/${userId}/organization`;
    return apiService.GET(url, params);
};

/** 會員子系統授權（member_system_access） */
export const getMemberSystemAccess = async (userId) => {
    return apiService.GET(`/api/admin/users/${userId}/system-access`);
};

export const grantMemberSystemAccess = async (
    userId,
    systemCode,
    body = {}
) => {
    return apiService.POST(
        `/api/admin/users/${userId}/system-access/${systemCode}/grant`,
        body
    );
};

export const revokeMemberSystemAccess = async (userId, systemCode, body) => {
    return apiService.POST(
        `/api/admin/users/${userId}/system-access/${systemCode}/revoke`,
        body
    );
};
